import { ecorEnv } from '@/../environments/environment';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { Component, HostListener, Inject, OnDestroy, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate, CommonModule } from '@angular/common';
import { CalendarDialogHttpService } from '../../services/calendar-dialog-http.service';
import { ExportSurveyDialogData } from '../../dialogs-data/export-survey-dialog-data';
import { ExportSurveyDialogService } from '../../services/export-survey-dialog.service';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-calendar-dialogs',
    templateUrl: './calendar-dialog.component.html',
    styleUrls: ['./calendar-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule
    ],
    providers: [
        ParadataEventService,
        CalendarDialogHttpService,
        ExportSurveyDialogService
    ]
})
export class CalendarDialogComponent implements OnInit, OnDestroy {
    protected destroyRef = inject(DestroyRef);
    private store = inject(Store<PortalUserState>);
    private paradataEventService = inject(ParadataEventService);
    private calendarDialogHttpService = inject(CalendarDialogHttpService);
    private exportSurveyDialogService = inject(ExportSurveyDialogService);
    private dialogRef = inject(MatDialogRef<CalendarDialogComponent>);

    public surveyInstanceId = inject<number>(MAT_DIALOG_DATA);

    // Signals for reactive state
    private user = signal<PortalUser | null>(null);
    public exporting = signal<boolean>(false);
    private fileName = signal<string>('');
    private file = signal<Blob | null>(null);
    private fileContent = signal<string>('');
    private emailSent = signal<boolean>(false);

    // Computed properties
    currentUser = computed(() => this.user());
    isExporting = computed(() => this.exporting());
    currentFileName = computed(() => this.fileName());
    currentFile = computed(() => this.file());
    currentFileContent = computed(() => this.fileContent());
    isEmailSent = computed(() => this.emailSent());

    constructor() { 
        this.dialogRef.disableClose = true;
        this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(user => {
                this.user.set(user);
            });
    }

    ngOnInit() {
        if (this.surveyInstanceId) {
            this.generateCalendar([], this.surveyInstanceId);
        }

    }

    generateCalendar(surveyTypes?: string[], surveyInstanceId?: number) {
        this.exporting.set(true);
        const currentUser = this.currentUser();
        if (currentUser) {
            this.calendarDialogHttpService.getSurveyCalendarReport(
                currentUser.userAccountId, surveyTypes, surveyInstanceId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        this.exporting.set(false);
                        this.fileName.set(currentUser.firstName + '_' + currentUser.lastName + '_survey_reminder_' + formatDate(new Date(), 'MM-dd-yyyy', 'en-US'));
                        const fileBlob = new Blob([response], { type: 'application/text' });
                        this.file.set(fileBlob);
                        fileBlob.text().then(text => {
                            this.fileContent.set(text);
                            this.sendCalendar();
                        });
                    },
                    error: (error) => {
                        this.exporting.set(false);
                        console.error('calendar-dialogs', 'generateCalendar', error);
                    }
                });
        }
    }

    sendCalendar() {
        const currentUser = this.currentUser();
        const currentFileContent = this.currentFileContent();
        const currentFileName = this.currentFileName();
        
        if (currentUser) {
            this.calendarDialogHttpService.sendCalendarReport(
                currentUser.userAccountId, currentFileContent, currentFileName)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        let isMailSentSuccessfully = response.isMailSentSuccessfully;
                        if (isMailSentSuccessfully && isMailSentSuccessfully === 'true') {
                            this.emailSent.set(true);
                            this.calendarSentSuccess();
                            this.close(true);
                        } else {
                            this.emailSent.set(false);
                            this.calendarSentFail();
                            this.close(true);
                        }
                    },
                    error: (error) => {
                        this.emailSent.set(false);
                        this.calendarSentFail();
                        this.close(true);
                        console.error('calendar-dialogs', 'sendCalendar', error);
                    }
                });
        }
    }

    calendarSentSuccess() {
        const options: ExportSurveyDialogData = {
            title: 'Export Due Date to Calendar',
            subheading: 'Success! Your calendar reminder has been sent to your email!',
            message: 'Please check your email associated with this account to add the survey reminders to your personal calendar. <br> <b>IMPORTANT NOTE:</b> If the survey due date is extended, your personal calendar will not sync automatically.',
            instructionHeading: 'Export Instructions:',
            instructions: [ {platform: 'Gmail', instructionLink: 'https://support.google.com/calendar/answer/37118?hl=en&co=GENIE.Platform%3DDesktop'},
                {platform: 'Outlook', instructionLink: 'https://support.microsoft.com/en-us/office/import-calendars-into-outlook-8e8364e1-400e-4c0f-a573-fe76b5a2d379'},
                {platform: 'iCloud', instructionLink: 'https://support.apple.com/guide/calendar/import-or-export-calendars-icl1023/mac'},
                {platform: 'Yahoo', instructionLink: 'https://help.yahoo.com/kb/new-mail-for-desktop/add-edit-delete-calendar-events-yahoo-mail-sln28062.html'} ],
            cancelButtonLabel: 'OK'
        };
        this.exportSurveyDialogService.open(options);
    }

    calendarSentFail() {
        const options: ExportSurveyDialogData = {
            title: 'Export Due Date to Calendar Failed',
            subheading: 'Something went wrong.',
            message: 'Please try again later.',
            cancelButtonLabel: 'OK'
        };
        this.exportSurveyDialogService.open(options);
    }

    close(value: any) {
        this.dialogRef.close(value);
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
    }
}